import { useState } from "react";
import { api } from "../api";
import type { Recipient } from "../../shared/types";

export function RecipientsPanel({
  recipients,
  senderEmail,
  onChanged,
}: {
  recipients: Recipient[];
  senderEmail: string;
  onChanged: () => void;
}) {
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function add(event: React.FormEvent) {
    event.preventDefault();
    const value = email.trim();
    if (!value || busy) return;
    setBusy(true);
    setError(null);
    try {
      await api.addRecipient(value);
      setEmail("");
      onChanged();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not add recipient");
    } finally {
      setBusy(false);
    }
  }

  async function remove(recipient: Recipient) {
    setError(null);
    try {
      await api.deleteRecipient(recipient.id);
      onChanged();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not remove recipient");
    }
  }

  return (
    <div className="panel">
      <div className="panel-title">Email notifications</div>
      <div className="panel-sub">
        Everyone here gets one digest per poll, sent from <span className="mono">{senderEmail}</span>
      </div>

      <form className="row-form" style={{ marginTop: 13 }} onSubmit={add}>
        <input
          className="input"
          type="email"
          value={email}
          placeholder="you@example.com"
          maxLength={254}
          onChange={(event) => setEmail(event.target.value)}
        />
        <button className="btn btn-primary" type="submit" disabled={!email.trim() || busy}>
          Add
        </button>
      </form>

      {error ? (
        <div className="error" style={{ marginTop: 9 }}>
          {error}
        </div>
      ) : null}

      <div className="list" style={{ marginTop: 13 }}>
        {recipients.length === 0 ? (
          <div className="empty">Nobody is being notified</div>
        ) : (
          recipients.map((recipient) => (
            <div className="list-row" key={recipient.id}>
              <span style={{ minWidth: 0, display: "flex", alignItems: "center", gap: 8 }}>
                <span
                  className="dot"
                  style={{ background: recipient.active ? "var(--accent)" : "var(--dim2)" }}
                  title={recipient.active ? "Active" : "Muted"}
                />
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {recipient.email}
                </span>
              </span>
              <span style={{ display: "flex", alignItems: "center", gap: 8, flex: "none" }}>
                <button
                  className="btn btn-tiny"
                  type="button"
                  onClick={() => void api.setRecipientActive(recipient.id, !recipient.active).then(onChanged)}
                >
                  {recipient.active ? "mute" : "unmute"}
                </button>
                <button className="btn btn-tiny" type="button" onClick={() => void remove(recipient)}>
                  ✕
                </button>
              </span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
